import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Download, X } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexProceso() {
    const [filters, setFilters] = useState({
        id_carrera: '',
        id_mencion: '',
        id_asignatura: '',
        id_paralelo: '',
        mes: new Date().getMonth() + 1,
        gestion: new Date().getFullYear()
    });

    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [asignaturas, setAsignaturas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [notas, setNotas] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');

    // Modal de descarga (Multi-mes)
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [selectedMeses, setSelectedMeses] = useState([]);

    // Semanas y subcampos
    const semanas = [1, 2, 3, 4];
    const campos = ['P1', 'P2', 'P3', 'T1', 'T2'];

    useEffect(() => { 
        loadCarreras(); 
        loadAsignaturas(); 
        verificarPeriodoCargadoNotas();
    }, []);

    useEffect(() => {
        if (filters.id_carrera) {
            loadMenciones(filters.id_carrera);
            loadAsignaturas(filters.id_carrera, null);
        } else {
            setMenciones([]);
            loadAsignaturas(null, null);
        }
    }, [filters.id_carrera]);

    useEffect(() => {
        if (filters.id_mencion) {
            loadAsignaturas(filters.id_carrera, filters.id_mencion);
        }
    }, [filters.id_mencion]);

    useEffect(() => {
        if (filters.id_asignatura) loadParalelos(filters.id_asignatura);
        else setParalelos([]);
    }, [filters.id_asignatura]);

    useEffect(() => {
        if (filters.id_asignatura && filters.id_paralelo) loadEstudiantes();
    }, [filters.id_asignatura, filters.id_paralelo, filters.mes, filters.gestion]);

    const verificarPeriodoCargadoNotas = async () => {
        try {
            const paramName = 'Cargado de notas Licenciatura > Proceso (docente)';
            const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent(paramName)}`);
            const data = await response.json();
            
            if (!data.habilitado) {
                setInscripcionCerrada(true);
                setMensajeCierre(data.mensaje || 'El periodo de cargado de notas de proceso se encuentra cerrado.');
            } else {
                setInscripcionCerrada(false);
            }
        } catch (error) {
            console.error('Error verificando periodo:', error);
        }
    };

    const loadCarreras = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/carreras`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setCarreras(Array.isArray(data.carreras) ? data.carreras : []);
        } catch (e) { console.error('Error:', e); setCarreras([]); }
    };

    const loadMenciones = async (id_carrera) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/menciones/${id_carrera}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setMenciones(Array.isArray(data.menciones) ? data.menciones : []);
        } catch (e) { console.error('Error:', e); setMenciones([]); }
    };

    const loadAsignaturas = async (carrera, mencion) => {
        try {
            const token = localStorage.getItem('token');
            const query = new URLSearchParams();
            if (carrera) query.append('carrera', carrera);
            if (mencion) query.append('mencion', mencion);
            query.append('gestion', filters.gestion);
            const res = await fetch(`${API_URL}/docente/asignaturas?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setAsignaturas(Array.isArray(data.asignaturas) ? data.asignaturas : []);
        } catch (e) { console.error('Error:', e); setAsignaturas([]); }
    };

    const loadParalelos = async (id_asignatura) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}?gestion=${filters.gestion}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setParalelos(Array.isArray(data.paralelos) ? data.paralelos : []);
        } catch (e) { console.error('Error:', e); setParalelos([]); }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion,
                mes: filters.mes,
                tipo: 'matriculacion'
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setEstudiantes(data.estudiantes || []);
            await loadNotas();
        } catch (e) { console.error('Error:', e); }
        finally { setLoading(false); }
    };

    const loadNotas = async () => {
        try {
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion,
                mes: filters.mes,
                tipo: 'proceso'
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/docente/notas-proceso?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            const map = {};
            (data.notas || []).forEach(n => {
                const key = `${n.id_estudiante}_${n.semana}_${n.campo}`;
                map[key] = n.valor || '';
            });
            setNotas(map);
        } catch (e) { console.error('Error:', e); }
    };

    const handleNotaChange = (id_estudiante, semana, campo, valor) => {
        const numVal = parseFloat(valor);
        let maxVal = 100;
        if (['P1', 'P2', 'P3'].includes(campo)) maxVal = 70;
        else if (['T1', 'T2'].includes(campo)) maxVal = 30;

        if (valor !== '' && (isNaN(numVal) || numVal < 0 || numVal > maxVal)) return;
        const key = `${id_estudiante}_${semana}_${campo}`;
        setNotas(prev => ({ ...prev, [key]: valor }));
    };

    const getNota = (id_estudiante, semana, campo) => {
        return notas[`${id_estudiante}_${semana}_${campo}`] || '';
    };

    const calcularTotales = (id_estudiante) => {
        let cantPracticas = 0;
        let sumaPracticas = 0;
        let cantTeoricas = 0;
        let sumaTeoricas = 0;

        semanas.forEach(semana => {
            ['P1', 'P2', 'P3'].forEach(campo => {
                const val = getNota(id_estudiante, semana, campo);
                if (val !== '') {
                    const num = parseFloat(val);
                    if (num >= 0) {
                        cantPracticas++;
                        sumaPracticas += num;
                    }
                }
            });
            ['T1', 'T2'].forEach(campo => {
                const val = getNota(id_estudiante, semana, campo);
                if (val !== '') {
                    const num = parseFloat(val);
                    if (num >= 0 && num <= 30) {
                        cantTeoricas++;
                        sumaTeoricas += num;
                    }
                }
            });
        });

        const promPractica = cantPracticas > 0 ? Math.round(sumaPracticas / cantPracticas) : 0;
        const promTeoria = cantTeoricas > 0 ? Math.round(sumaTeoricas / cantTeoricas) : 0;
        const total = promPractica + promTeoria;

        return {
            cantPracticas,
            promPractica,
            cantTeoricas,
            promTeoria,
            total
        };
    };

    const handleGuardar = async () => {
        try {
            setSaving(true);
            const gestion = filters.gestion || new Date().getFullYear();
            const entries = [];

            estudiantes.forEach(est => {
                semanas.forEach(semana => {
                    campos.forEach(campo => {
                        const valor = getNota(est.id_estudiante, semana, campo);
                        if (valor !== '') {
                            entries.push({
                                id_estudiante: est.id_estudiante,
                                id_asignatura: filters.id_asignatura,
                                id_paralelo: filters.id_paralelo,
                                semana, campo, valor: parseFloat(valor), gestion,
                                mes: filters.mes
                            });
                        }
                    });
                });
            });

            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/docente/notas-proceso/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ notas: entries })
            });

            alert('Notas guardadas exitosamente');
        } catch (e) { console.error('Error:', e); alert('Error al guardar'); }
        finally { setSaving(false); }
    };

    const toggleMes = (m) => {
        setSelectedMeses(prev => 
            prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
        );
    };

    const handleDownload = async () => {
        try {
            if (selectedMeses.length === 0) {
                alert('Seleccione al menos un mes para descargar');
                return;
            }

            const token = localStorage.getItem('token');
            const query = new URLSearchParams({ 
                meses: selectedMeses.join(','), 
                tipo: 'proceso',
                id_asignatura: filters.id_asignatura,
                id_paralelo: filters.id_paralelo,
                gestion: filters.gestion
            });
            
            const res = await fetch(`${API_URL}/docente/notas-proceso/export?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Error en el servidor al exportar notas');
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `notas_proceso_${filters.id_asignatura}_${filters.gestion}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            setShowDownloadModal(false);
        } catch (e) {
            console.error('Error al descargar:', e);
            alert(`Error al descargar: ${e.message}`);
        }
    };

    const filteredEstudiantes = estudiantes.filter(est => {
        const s = searchTerm.toLowerCase();
        return (est.codigo || '').toLowerCase().includes(s) ||
            (est.nombres || '').toLowerCase().includes(s) ||
            (est.apellidos || '').toLowerCase().includes(s);
    });

    return (
        <div className="p-4 md:p-6 max-w-full mx-auto">
            <h1 className="text-xl font-bold text-gray-800 mb-6">Kardex &gt; Calificaciones &gt; Proceso</h1>

            {/* Filtros */}
            <div className="flex flex-wrap gap-2 mb-6">
                {/* Gestión */}
                <div className="relative">
                    <select value={filters.gestion} onChange={(e) => setFilters({ ...filters, gestion: parseInt(e.target.value) })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px]">
                        {[2024, 2025, 2026, 2027].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Carrera */}
                <div className="relative">
                    <select value={filters.id_carrera} onChange={(e) => setFilters({ ...filters, id_carrera: e.target.value, id_mencion: '', id_asignatura: '', id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px]">
                        <option value="">Carrera</option>
                        {carreras.map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Mención */}
                <div className="relative">
                    <select value={filters.id_mencion} onChange={(e) => setFilters({ ...filters, id_mencion: e.target.value, id_asignatura: '', id_paralelo: '' })}
                        disabled={!filters.id_carrera} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[150px] disabled:bg-gray-50">
                        <option value="">Mención</option>
                        {menciones.map(m => <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Asignatura */}
                <div className="relative">
                    <select value={filters.id_asignatura} onChange={(e) => setFilters({ ...filters, id_asignatura: e.target.value, id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[200px]">
                        <option value="">Asignatura</option>
                        {asignaturas.map(a => (
                            <option key={a.id_asignatura} value={a.id_asignatura}>
                                {a.nombre} {a.codigo ? `(${a.codigo})` : ''}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Paralelo */}
                <div className="relative">
                    <select value={filters.id_paralelo} onChange={(e) => setFilters({ ...filters, id_paralelo: e.target.value })}
                        disabled={!filters.id_asignatura} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[100px] disabled:bg-gray-100">
                        <option value="">Paralelo</option>
                        {paralelos.map(p => <option key={p.id_paralelo || p.paralelo} value={p.id_paralelo || p.paralelo}>{p.paralelo || p.nombre}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative">
                    <select value={filters.mes} onChange={(e) => setFilters({ ...filters, mes: parseInt(e.target.value) })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[120px]">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                            const p = Math.ceil(m / 3);
                            return (
                                <option key={m} value={m}>
                                    Mes {m} (P{p})
                                </option>
                            );
                        })}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                <div className="relative flex-1 max-w-xs">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm" />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>

                <button onClick={() => setShowDownloadModal(true)} className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700" title="Exportar Reporte">
                    <Download size={18} />
                </button>
            </div>

            {/* Mensaje de Periodo Cerrado */}
            {inscripcionCerrada && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium">{mensajeCierre}</p>
                </div>
            )}

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th rowSpan={2} className="px-2 py-2 border text-center font-semibold">No</th>
                            <th rowSpan={2} className="px-2 py-2 border text-center font-semibold">Código</th>
                            <th rowSpan={2} className="px-2 py-2 border text-center font-semibold">Nombres</th>
                            <th rowSpan={2} className="px-2 py-2 border text-center font-semibold">Apellidos</th>
                            {semanas.map(s => <th key={s} colSpan={5} className="px-2 py-2 border text-center font-semibold bg-gray-100">Semana {s}</th>)}
                            <th colSpan={5} className="px-2 py-2 border text-center font-semibold bg-blue-50">TOTAL</th>
                        </tr>
                        <tr>
                            {semanas.map(s => campos.map(c => <th key={`${s}_${c}`} className="px-1 py-1 border text-center font-medium text-xs">{c}</th>))}
                            <th className="px-1 py-2 border text-center font-medium text-xs align-bottom"><div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>CANTIDAD PRACTICAS</div></th>
                            <th className="px-1 py-2 border text-center font-medium text-xs align-bottom"><div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PRACTICA</div></th>
                            <th className="px-1 py-2 border text-center font-medium text-xs align-bottom"><div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>CANTIDAD TEORICAS</div></th>
                            <th className="px-1 py-2 border text-center font-medium text-xs align-bottom"><div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>TEORIA</div></th>
                            <th className="px-1 py-2 border text-center font-medium text-xs align-bottom"><div className="mx-auto" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>TOTAL</div></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {loading ? (
                            <tr><td colSpan={4 + semanas.length * campos.length + 5} className="py-8 text-center text-gray-500">Cargando...</td></tr>
                        ) : filteredEstudiantes.length === 0 ? (
                            <tr><td colSpan={4 + semanas.length * campos.length + 5} className="py-8 text-center text-gray-500">
                                {filters.id_paralelo ? 'No hay estudiantes inscritos en este paralelo.' : 'Seleccione una asignatura y un paralelo para ver los estudiantes.'}
                            </td></tr>
                        ) : filteredEstudiantes.map((est, i) => {
                            const totales = calcularTotales(est.id_estudiante);
                            return (
                                <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                    <td className="px-2 py-1 border text-center">{i + 1}</td>
                                    <td className="px-2 py-1 border text-center font-medium">{est.codigo || '-'}</td>
                                    <td className="px-2 py-1 border text-center font-medium">{est.nombres || '-'}</td>
                                    <td className="px-2 py-1 border text-center font-medium">{est.apellidos || '-'}</td>
                                    {semanas.map(semana => campos.map(campo => (
                                        <td key={`${est.id_estudiante}_${semana}_${campo}`} className="px-1 py-1 border text-center">
                                            <input type="number" min="0" max={['P1', 'P2', 'P3'].includes(campo) ? 70 : 30} value={getNota(est.id_estudiante, semana, campo)}
                                                onChange={(e) => handleNotaChange(est.id_estudiante, semana, campo, e.target.value)}
                                                className="w-10 px-1 py-0.5 border border-gray-300 rounded text-center text-xs" />
                                        </td>
                                    )))}
                                    <td className="px-2 py-1 border text-center font-medium bg-blue-50/30 text-xs">{totales.cantPracticas}</td>
                                    <td className="px-2 py-1 border text-center font-medium bg-blue-50/30 text-xs">{totales.promPractica}</td>
                                    <td className="px-2 py-1 border text-center font-medium bg-blue-50/30 text-xs">{totales.cantTeoricas}</td>
                                    <td className="px-2 py-1 border text-center font-medium bg-blue-50/30 text-xs">{totales.promTeoria}</td>
                                    <td className="px-2 py-1 border text-center font-bold bg-blue-100/50 text-xs">{totales.total}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>

                {filteredEstudiantes.length > 0 && (
                    <div className="p-4 border-t flex justify-end">
                        <button onClick={handleGuardar} disabled={saving}
                            className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 disabled:opacity-50">
                            {saving ? 'Guardando...' : 'Guardar Notas'}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal Descarga Multi-mes */}
            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Descargar Reporte de Notas</h2>
                                <p className="text-xs text-gray-500">Gestión seleccionada: {filters.gestion}</p>
                            </div>
                            <button onClick={() => setShowDownloadModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-medium mb-4 text-gray-700">Seleccione los meses a incluir en el Excel:</label>
                            <div className="grid grid-cols-3 gap-3">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                                    const p = Math.ceil(m / 3);
                                    const isSelected = selectedMeses.includes(m);
                                    return (
                                        <button key={m} type="button" onClick={() => toggleMes(m)}
                                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                                isSelected 
                                                ? 'bg-blue-50 border-blue-600 text-blue-700' 
                                                : 'bg-white border-gray-100 text-gray-600 hover:border-gray-300'
                                            }`}>
                                            <span className="text-xs font-bold">Mes {m}</span>
                                            <span className="text-[10px] opacity-70">P{p}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-6 flex gap-2">
                                <button type="button" onClick={() => setSelectedMeses([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])}
                                    className="text-xs text-blue-600 hover:underline">Seleccionar todo</button>
                                <span className="text-gray-300">|</span>
                                <button type="button" onClick={() => setSelectedMeses([])}
                                    className="text-xs text-red-600 hover:underline">Limpiar</button>
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t gap-3 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setShowDownloadModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition">
                                Cancelar
                            </button>
                            <button onClick={handleDownload} disabled={selectedMeses.length === 0}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md font-medium hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md">
                                Descargar Reporte
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
